<script lang="ts">
  // BJL-CHECK: Card (used, one per stop row), Badge (used, bus badges), Button (used, close),
  // List. Each stop is a clickable Card, which supplies the button semantics and keyboard
  // handling a hand-rolled row would declare itself. The roundels stay custom:
  // circle-vs-diamond is the MTA's local/express distinction, i.e. data.
  // See artifacts/deviations/NearbyStationsSheet.md.
  //
  // The stops closest to the user: subway stations with their bullets, then bus stops with
  // the lines calling there. Tapping a row hands the selection to the matching detail sheet.
  import { Button } from '@byjackli-labs/ui/Button'
  import { Card } from '@byjackli-labs/ui/Card'
  import { Badge } from '@byjackli-labs/ui/Badge'
  import { Colors, getRouteColor } from '../../ui/colors'
  import { bulletFontPx, bulletText, panelFrame, Type } from '../../ui/theme'
  import { makeNearestStations, type NearestStation } from '../../lib/nearest'
  import { useStopsStore } from '../../store/stops'
  import { storeSelect } from '../../lib/store.svelte'
  import type { StationInfo, BusStopInfo } from './stationInfo'

  import RouteBadge from '../RouteBadge.svelte'

  interface Props {
    userLocation: { latitude: number; longitude: number } | null
    onSelectStation: (station: StationInfo) => void
    onSelectBusStop: (stop: BusStopInfo) => void
    onClose: () => void
  }

  const { userLocation, onSelectStation, onSelectBusStop, onClose }: Props = $props()

  function formatDistance(meters: number): string {
    const feet = meters * 3.28084
    // Under ~0.1 mi, feet reads better than a "0.0 mi" rounding: to the nearest 10, to
    // avoid false precision. Above that, miles to one decimal.
    if (feet < 528) return `${Math.round(feet / 10) * 10} ft`
    return `${(meters / 1609.34).toFixed(1)} mi`
  }

  const subwayStops = storeSelect(useStopsStore, (s) => s.stops)
  const busStops = storeSelect(useStopsStore, (s) => s.busStops)

  // One scanner per source, each with its own throttle cache, so the bus scan (~15k stops)
  // isn't invalidated by the subway scan on the same GPS tick.
  const scanSubway = makeNearestStations()
  const scanBus = makeNearestStations()

  const nearestSubway = $derived(scanSubway(subwayStops.value, userLocation, { kind: 'subway' }))
  const nearestBus = $derived(scanBus(busStops.value, userLocation, { kind: 'bus' }))

  // Bus stops load lazily and may be absent on a subway-filtered session, so ask TransitMap
  // (the fetch owner) for them.
  const busStopsLoaded = $derived(busStops.value.length > 0)
  $effect(() => {
    useStopsStore.getState().requestBusStops()
  })
</script>

<!-- Small non-interactive route bullet using unified RouteBadge -->
{#snippet routeBullet(routeId: string)}
  <RouteBadge {routeId} size={22} />
{/snippet}

<!-- Bus route badge: a bjl Tag carrying the route's own MTA color. Bus ids are short
     strings ("M15", "Bx12", "S79") that don't fit a roundel. -->
{#snippet busRouteBadge(routeId: string)}
  <Badge size="xs" register="mono" colors={{ bg: getRouteColor(routeId), text: '#fff' }}>
    {routeId}
  </Badge>
{/snippet}

<!-- One tappable row: stop name, the lines serving it (subway roundels or bus badges), and
     the walking distance. -->
{#snippet stopRow(entry: NearestStation, isBus: boolean, onSelect: () => void)}
  <Card
    clickable
    onclick={onSelect}
    ariaLabel="Open {entry.station.name}"
    class="nearby-stop-card"
  >
    <div style="display: flex; align-items: center; gap: 12px; width: 100%; text-align: left;">
    <div style="flex: 1; min-width: 0;">
      <div
        style="
          color: {Colors.text}; {Type.title};
          overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
        "
      >{entry.station.name}</div>
      {#if entry.station.routes.length > 0}
        <div style="display: flex; flex-wrap: wrap; gap: 4px; margin-top: 6px;">
          {#each entry.station.routes as r (r)}
            {#if isBus}{@render busRouteBadge(r)}{:else}{@render routeBullet(r)}{/if}
          {/each}
        </div>
      {/if}
    </div>
    <span style="color: {Colors.textSecondary}; {Type.body}; white-space: nowrap;">
      {formatDistance(entry.distanceMeters)}
    </span>
    </div>
  </Card>
{/snippet}

{#snippet sectionHeader(label: string)}
  <div
    style="
      padding: 10px 16px 6px; background-color: {Colors.ground};
      color: {Colors.textSecondary}; {Type.eyebrow};
      position: sticky; top: 0; z-index: 1;
    "
  >{label}</div>
{/snippet}

<!-- Below the detail sheets (StationSheet/VehicleSheet at 1000) but above the map. -->
<div
  style="
    position: absolute; left: 0; right: 0; bottom: 0;
    z-index: 200;
    max-height: 55%; display: flex; flex-direction: column;
    background-color: {Colors.ground};
    {panelFrame('top')}
    box-shadow: 0 -4px 24px rgba(0,0,0,0.5);
  "
>
  <div
    style="
      display: flex; align-items: center; justify-content: space-between;
      padding: 14px 16px 10px; border-bottom: 1px solid {Colors.border};
    "
  >
    <span style="color: {Colors.text}; {Type.title};">Nearby stops</span>
    <Button variant="text" size="sm" iconOnly onclick={onClose} aria-label="Close">✕</Button>
  </div>

  <div style="flex: 1; min-height: 0; overflow-y: auto; padding: 0 12px 12px; display: flex; flex-direction: column; gap: 6px;">
    {#if !userLocation}
      <div
        style="padding: 24px 16px; color: {Colors.textSecondary}; {Type.bodyLg}; text-align: center;"
      >
        Waiting for your location…
      </div>
    {:else}
      <div style="display: flex; flex-direction: column; gap: 8px;">
        {@render sectionHeader('Subway stations')}
        {#if nearestSubway.length === 0}
          <div style="padding: 14px 16px; color: {Colors.textSecondary}; {Type.body};">
            No stations within walking distance.
          </div>
        {:else}
          {#each nearestSubway as entry (entry.station.id)}
            {@render stopRow(entry, false, () =>
              onSelectStation({
                id: entry.station.id,
                name: entry.station.name,
                routes: entry.station.routes,
              }),
            )}
          {/each}
        {/if}
      </div>

      <div style="display: flex; flex-direction: column; gap: 8px;">
        {@render sectionHeader('Bus stops')}
        {#if nearestBus.length === 0}
          <div style="padding: 14px 16px; color: {Colors.textSecondary}; {Type.body};">
            {busStopsLoaded
              ? 'No bus stops within walking distance.'
              : 'Loading nearby bus stops…'}
          </div>
        {:else}
          {#each nearestBus as entry (entry.station.id)}
            {@render stopRow(entry, true, () =>
              onSelectBusStop({ id: entry.station.id, name: entry.station.name }),
            )}
          {/each}
        {/if}
      </div>
    {/if}
  </div>
</div>
